import { useState, useEffect, useRef } from 'react';
import { Flame, Snowflake, Trophy, Shield, AlertTriangle, Check } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

interface StreakData {
  current: number;
  longest: number;
  lastActivity: string | null;
  isActiveToday: boolean;
  canExtendToday: boolean;
  atRisk?: boolean;
  wasBroken?: boolean;
}

interface FreezeData {
  available: number;
  usedThisWeek: number;
  isActive: boolean;
  justUsed: boolean;
}

interface Milestone {
  milestone: number;
  label: string;
  achieved: boolean;
  achievedAt: string | null;
}

export default function StreakPanel() {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [streak, setStreak] = useState<StreakData | null>(null);
  const [freeze, setFreeze] = useState<FreezeData | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [freezing, setFreezing] = useState(false);
  const [showFreezeSuccess, setShowFreezeSuccess] = useState(false);
  const dataLoadedRef = useRef(false);

  // Load data once when user becomes available
  useEffect(() => {
    // Only load once
    if (dataLoadedRef.current) return;

    if (user) {
      dataLoadedRef.current = true;
      loadData();
    } else {
      setLoading(false);
    }
  }, [user]);

  async function loadData() {
    try {
      const [streakData, milestonesData] = await Promise.all([
        fetchApi<{ streak: StreakData; freeze: FreezeData }>('/api/streaks/me'),
        fetchApi<{ milestones: Milestone[] }>('/api/streaks/milestones')
      ]);
      setStreak(streakData.streak);
      setFreeze(streakData.freeze);
      setMilestones(milestonesData.milestones);

      // Show freeze notification if just used
      if (streakData.freeze.justUsed) {
        setShowFreezeSuccess(true);
        setTimeout(() => setShowFreezeSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Failed to load streak data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function activateFreeze() {
    if (!freeze || freeze.available <= 0 || freeze.isActive || !streak?.current) {
      return;
    }

    setFreezing(true);
    try {
      await fetchApi('/api/streaks/freeze', { method: 'POST' });
      await loadData();
      setShowFreezeSuccess(true);
      setTimeout(() => setShowFreezeSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to activate freeze:', error);
    } finally {
      setFreezing(false);
    }
  }

  if (!user || loading) {
    return null;
  }

  if (!streak) {
    return (
      <div className="bg-background-secondary rounded-xl border border-gray-800 p-5">
        <p className="text-gray-400 text-sm">Start learning to build your streak!</p>
      </div>
    );
  }

  const nextMilestone = milestones.find(m => !m.achieved);
  const progress = nextMilestone ? (streak.current / nextMilestone.milestone) * 100 : 100;
  const isOnFire = streak.current >= 7;

  return (
    <div className="bg-background-secondary rounded-xl border border-gray-800 overflow-hidden">
      {/* Header */}
      <div className={`p-5 ${
        freeze?.isActive
          ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10'
          : isOnFire
            ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10'
            : ''
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {freeze?.isActive ? (
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Snowflake className="w-6 h-6 text-cyan-400" />
              </div>
            ) : (
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isOnFire ? 'bg-orange-500/20' : 'bg-gray-800'
              }`}>
                <Flame className={`w-6 h-6 ${isOnFire ? 'text-orange-400' : 'text-gray-400'}`} />
              </div>
            )}
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-white">{streak.current}</span>
                <span className="text-gray-400">day{streak.current !== 1 ? 's' : ''}</span>
              </div>
              <p className="text-sm text-gray-500">
                Best: {streak.longest} day{streak.longest !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Streak status indicator */}
          <div className="text-right">
            {freeze?.isActive ? (
              <div className="flex items-center gap-1.5 text-cyan-400 text-sm">
                <Shield className="w-4 h-4" />
                <span>Protected</span>
              </div>
            ) : streak.isActiveToday ? (
              <div className="flex items-center gap-1.5 text-green-400 text-sm">
                <Check className="w-4 h-4" />
                <span>Active today</span>
              </div>
            ) : streak.current > 0 ? (
              <div className="flex items-center gap-1.5 text-yellow-400 text-sm animate-pulse">
                <AlertTriangle className="w-4 h-4" />
                <span>At risk!</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Freeze notification */}
        {showFreezeSuccess && (
          <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 text-sm flex items-center gap-2">
            <Snowflake className="w-4 h-4" />
            <span>Streak freeze activated! Your {streak.current}-day streak is protected.</span>
          </div>
        )}

        {/* Freeze section */}
        {freeze && (
          <div className="p-3 bg-gray-800/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Snowflake className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-gray-300">Streak Freeze</span>
              </div>
              <span className="text-sm text-gray-400">
                {freeze.available} available this week
              </span>
            </div>

            {streak.current > 0 && !freeze.isActive && freeze.available > 0 && (
              <button
                onClick={activateFreeze}
                disabled={freezing || !streak.current}
                className="mt-3 w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {freezing ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Protect my streak
                  </>
                )}
              </button>
            )}

            {freeze.isActive && (
              <p className="mt-2 text-xs text-cyan-400/70">
                Your streak is protected until your next activity.
              </p>
            )}

            {freeze.available === 0 && !freeze.isActive && (
              <p className="mt-2 text-xs text-gray-500">
                Freezes reset every Monday. Used {freeze.usedThisWeek} this week.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Milestones */}
      {milestones.length > 0 && (
        <div className="p-5 border-t border-gray-800">
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" />
            Milestones
          </h3>

          {/* Progress to next milestone */}
          {nextMilestone && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>{streak.current} / {nextMilestone.milestone} days</span>
                <span>{nextMilestone.label}</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-accent-blue to-accent-purple transition-all duration-500"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Milestone badges */}
          <div className="flex flex-wrap gap-2">
            {milestones.map(m => (
              <div
                key={m.milestone}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  m.achieved
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-gray-800 text-gray-500 border border-gray-700'
                }`}
                title={m.achieved && m.achievedAt ? `Achieved ${new Date(m.achievedAt).toLocaleDateString()}` : `Reach ${m.milestone} days`}
              >
                {m.achieved && <Trophy className="w-3 h-3 inline mr-1" />}
                {m.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
